import React, { useEffect, useMemo, useState } from "react";

const PAGE_SIZE = 16;
const FALLBACK_AVATARS = Array.from({ length: 12 }, (_, i) => `/avatars/avatar${i + 1}.png`);

export default function AvatarPicker({ value, onChange }) {
  const [avatars, setAvatars] = useState(FALLBACK_AVATARS);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch("/avatars/manifest.json")
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        const list = (data.avatars || [])
          .filter(Boolean)
          .map((file) => `/avatars/${file}`);
        if (list.length) setAvatars(list);
      })
      .catch(() => {
        if (!cancelled) setAvatars(FALLBACK_AVATARS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(avatars.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
  }, [page, totalPages]);

  useEffect(() => {
    if (!value && avatars.length) onChange(avatars[0]);
  }, [avatars, value, onChange]);

  useEffect(() => {
    if (!value) return;
    const selectedIndex = avatars.indexOf(value);
    if (selectedIndex >= 0) {
      const selectedPage = Math.floor(selectedIndex / PAGE_SIZE);
      if (selectedPage !== page) setPage(selectedPage);
    }
  }, [value, avatars]);

  const pageAvatars = useMemo(
    () => avatars.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [avatars, page]
  );

  return (
    <div className="guts-avatar-picker">
      <div className="guts-avatar-grid">
        {pageAvatars.map((url, idx) => {
          const globalIndex = page * PAGE_SIZE + idx;
          const selected = value === url;
          return (
            <button
              key={url}
              type="button"
              className={`guts-avatar-option${selected ? " is-selected" : ""}`}
              onClick={() => onChange(url)}
              aria-label={`Avatar ${globalIndex + 1}`}
              aria-pressed={selected}
            >
              <img src={url} alt={`Avatar ${globalIndex + 1}`} loading="lazy" />
            </button>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div className="guts-avatar-pagination">
          <button
            type="button"
            className="guts-avatar-page-btn"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </button>
          <span className="guts-avatar-page-label">
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            className="guts-avatar-page-btn"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
